/**
 * Adaptadores puros Snapshot → datos de cada gráfica + su LECTURA de una línea (determinista, sin LLM).
 * Ningún número sale de aquí que no venga del motor; aquí solo se elige, ordena y redacta.
 */
import type { Flag, InvestableBucket, Snapshot } from "@/engine/types";
import { fmtMoney, fmtMoneyCorto, fmtMeses, fmtPct, fmtPts } from "@/engine/format/esMX";
import { isBlank } from "@/engine/excel/fns";

export type EstadoFig = "ok" | "parcial" | "vacio";
export interface Falta { cell: string; label: string }
export interface Fig<T> { estado: EstadoFig; lectura: string; faltan: Falta[]; datos: T }

const BUCKETS: InvestableBucket[] = ["Preservación", "Ingreso", "Crecimiento"];
const pct0 = (v: number | null) => fmtPct(v, 0);
const pct1 = (v: number | null) => fmtPct(v, 1);
const faltanDe = (s: Snapshot, ...hojas: string[]): Falta[] => s.completeness.missing.filter((m) => hojas.some((h) => m.cell.startsWith(h)));
const flag = (s: Snapshot, code: string): Flag | undefined => s.flags.find((f) => f.code === code);
const vacio = <T>(datos: T, faltan: Falta[]): Fig<T> => ({ estado: "vacio", lectura: "", faltan, datos });

// ───────────── 1 · Hero ─────────────
export function hero(s: Snapshot) {
  const cur = s.setup.currency;
  const B = s.balance;
  const hayActivos = B.totals.assets > 0 || B.totals.liabilities > 0;
  const personales = B.assets.filter((a) => a.investable === "No" && !isBlank(a.what)).sort((a, b) => (b.value ?? 0) - (a.value ?? 0)).slice(0, 2).map((a) => a.what as string);
  const datos = { netWorth: s.photo.netWorth, investableNet: B.totals.investableNet, outside: B.outside.total, personales, currency: cur };
  if (!hayActivos) return vacio(datos, faltanDe(s, "3B"));
  let lectura = `${fmtMoney(B.totals.investableNet, cur)} trabajan en tu portafolio`;
  if (B.outside.total > 0) lectura += `; ${fmtMoney(B.outside.total, cur)} están fuera del portafolio${personales.length ? ` (${personales.join(", ")})` : ""}`;
  if (B.totals.liabilities > 0) lectura += `; debes ${fmtMoney(B.totals.liabilities, cur)}`;
  return { estado: "ok" as EstadoFig, lectura: lectura + ".", faltan: [], datos };
}

// ───────────── 2 · KPIs ─────────────
export function kpis(s: Snapshot) {
  const cur = s.setup.currency;
  const ahorro = s.photo.savingsRate, libertad = s.photo.freedomPct, rend = s.derived.investableReturn, rendExcel = s.photo.expectedReturn;
  const difiere = rend !== null && rendExcel !== null && Math.abs(rend - rendExcel) > 0.01;
  return {
    ahorro: {
      valor: ahorro, marca: 0.2,
      lectura: ahorro === null ? "" : ahorro >= 0.2 ? `Ahorras ${pct1(ahorro)} de lo que ganas: arriba del 20% que pide el programa.` : ahorro < 0 ? `Gastas más de lo que ganas: te faltan ${fmtMoney(-s.flow.totals.netAnnual, cur)} al año.` : `Ahorras ${pct1(ahorro)} de lo que ganas; el programa pide arriba de 20%.`,
    },
    libertad: { valor: libertad, lectura: libertad === null ? "" : `Tus ingresos pasivos cubren ${pct1(libertad)} de tu gasto.` },
    rendimiento: {
      valor: rend ?? rendExcel, excel: rendExcel, corregido: rend, difiere,
      lectura: rend === null ? "" : difiere ? `Lo invertible rinde ${fmtPct(rend)}; el modelo muestra ${fmtPct(rendExcel)} porque también pondera lo de uso personal.` : `Tu portafolio invertible rinde ${fmtPct(rend)} esperado al año.`,
    },
    faltan: faltanDe(s, "3A", "3B"),
  };
}

// ───────────── 3 · Fondo de emergencia ─────────────
export function fondo(s: Snapshot) {
  const cur = s.setup.currency;
  const e = s.life.emergency;
  const datos = { ...e, meses: s.derived.monthsToCompleteEmergencyFund, flujo: s.derived.monthlyNetFlow, currency: cur };
  if (e.amount === null) return vacio(datos, [...faltanDe(s, "3A"), ...(e.months === null ? [{ cell: "2 · Vida!C28", label: "meses del fondo de emergencia" }] : [])]);
  const p = e.fundedPct ?? 0;
  let lectura: string;
  if (p >= 0.999) lectura = `Tu fondo de emergencia está completo: ${fmtMoney(e.amount, cur)} (${e.months} meses de gastos).`;
  else {
    lectura = `Tu fondo está al ${pct0(p)}: faltan ${fmtMoney(e.missing, cur)}`;
    if (s.derived.monthsToCompleteEmergencyFund !== null && s.derived.monthlyNetFlow !== null) lectura += `; con tu flujo de ${fmtMoney(s.derived.monthlyNetFlow, cur)} al mes lo completas en ${fmtMeses(s.derived.monthsToCompleteEmergencyFund)}.`;
    else if (s.derived.monthlyNetFlow !== null && s.derived.monthlyNetFlow <= 0) lectura += "; con tu flujo de hoy no se completa: primero el flujo.";
    else lectura += ".";
  }
  return { estado: "ok" as EstadoFig, lectura, faltan: [], datos };
}

// ───────────── 4 · Flujo ─────────────
export function flujo(s: Snapshot) {
  const cur = s.setup.currency, t = s.flow.totals;
  const datos = { activo: t.incomeActive, pasivo: t.incomePassive, sinTipo: t.unclassified, gasto: t.expenseAnnual, neto: t.netAnnual, currency: cur };
  if (t.incomeAnnual === 0 && t.expenseAnnual === 0) return vacio(datos, faltanDe(s, "3A"));
  const lectura = t.netAnnual >= 0
    ? `Ganas ${fmtMoney(t.incomeAnnual, cur)} al año y gastas ${fmtMoney(t.expenseAnnual, cur)}: te quedan ${fmtMoney(t.netAnnual, cur)}${t.savingsRate !== null ? ` (${pct0(t.savingsRate)})` : ""}.`
    : `Ganas ${fmtMoney(t.incomeAnnual, cur)} al año y gastas ${fmtMoney(t.expenseAnnual, cur)}: te faltan ${fmtMoney(-t.netAnnual, cur)}.`;
  return { estado: (t.incomeAnnual === 0 || t.expenseAnnual === 0 ? "parcial" : "ok") as EstadoFig, lectura, faltan: faltanDe(s, "3A"), datos };
}

// ───────────── 5 · Gastos por categoría ─────────────
export function gastos(s: Snapshot) {
  const cur = s.setup.currency;
  const filas = s.flow.byCategory.map((c) => ({ etiqueta: c.category, valor: c.annual, pct: c.pctOfExpense }));
  if (s.flow.totals.expenseAnnual === 0) return vacio({ filas, currency: cur }, faltanDe(s, "3A"));
  const top = [...filas].sort((a, b) => b.valor - a.valor)[0];
  return { estado: "ok" as EstadoFig, lectura: `${top.etiqueta} se lleva el ${pct0(top.pct)} de tu gasto (${fmtMoney(top.valor, cur)} al año).`, faltan: [], datos: { filas, currency: cur } };
}

// ───────────── 6 · Dónde está tu dinero ─────────────
export function balance(s: Snapshot) {
  const cur = s.setup.currency, B = s.balance;
  const segmentos = [
    ...BUCKETS.map((b) => ({ clave: b, valor: B.byBucket.find((x) => x.bucket === b)!.amount })),
    { clave: "personal", valor: B.outside.personal }, { clave: "business", valor: B.outside.business }, { clave: "contribution", valor: B.outside.contribution },
  ];
  const datos = { segmentos, total: B.totals.assets, invertible: B.totals.investableAssets, porBucket: B.byBucket, fuera: B.outside, currency: cur };
  if (B.totals.assets === 0) return vacio(datos, faltanDe(s, "3B"));
  const partes = B.byBucket.map((x) => `${pct0(x.pct)} ${x.bucket}`).join(", ");
  let lectura = B.totals.investableAssets > 0 ? `Tu portafolio invertible: ${fmtMoney(B.totals.investableAssets, cur)} (${partes}).` : "Todavía no tienes activos invertibles: todo lo que capturaste es de uso personal o de negocio.";
  if (B.outside.total > 0) lectura += ` Fuera del portafolio: ${fmtMoney(B.outside.total, cur)} (${pct0(B.outside.pctTotal)} de tus activos).`;
  return { estado: "ok" as EstadoFig, lectura, faltan: [], datos };
}

// ───────────── 7 · Allocation hoy vs objetivo ─────────────
export function allocation(s: Snapshot) {
  const cur = s.setup.currency;
  const filas = s.strategy.allocation.map((a) => {
    const banda = s.derived.ipsBands.find((b) => b.bucket === a.bucket);
    return { bucket: a.bucket, hoy: a.currentPct, objetivo: a.targetPct, brechaPts: a.gapPts, brechaMonto: a.gapAmount, banda: banda && banda.min !== null && banda.max !== null ? { min: banda.min, max: banda.max } : null };
  });
  const soloFondo = !!flag(s, "TARGET_ALLOC_ONLY_EMERGENCY");
  const datos = { filas, soloFondo, currency: cur };
  if (filas.every((f) => f.objetivo === null) || filas.every((f) => f.hoy === null)) return vacio(datos, [...faltanDe(s, "3B"), ...faltanDe(s, "2 · Vida!B29")]);
  const mayor = [...filas].filter((f) => f.brechaPts !== null).sort((a, b) => Math.abs(b.brechaPts!) - Math.abs(a.brechaPts!))[0];
  let lectura = mayor && Math.abs(mayor.brechaPts ?? 0) >= 0.005
    ? `La brecha más grande: ${mayor.bucket}, ${pct0(mayor.hoy)} hoy vs ${pct0(mayor.objetivo)} objetivo (${fmtPts(mayor.brechaPts)}, ${fmtMoney(Math.abs(mayor.brechaMonto ?? 0), cur)}).`
    : "Tu allocation de hoy ya está en su objetivo.";
  if (soloFondo) lectura += " Ojo: sin objetivos capturados, el objetivo es solo el del fondo de emergencia.";
  return { estado: (soloFondo ? "parcial" : "ok") as EstadoFig, lectura, faltan: soloFondo ? [{ cell: "2 · Vida!B29:B37", label: "tus objetivos patrimoniales" }] : [], datos };
}

// ───────────── 8 · Cascada ─────────────
export function cascada(s: Snapshot) {
  const cur = s.setup.currency;
  const neto = new Map((s.derived.cascadeNet ?? []).map((c) => [c.row, c.fundedPct]));
  const dom = new Map(s.derived.dominantBucketByObjective.map((d) => [d.row, d.bucket]));
  const filas = s.life.objectives.filter((o) => !isBlank(o.name) && (o.isEmergencyFund ? o.costToday !== null : true))
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
    .map((o) => ({
      clave: o.row, nombre: o.isEmergencyFund ? `Fondo de emergencia (${o.age ?? "—"} meses)` : (o.name as string), orden: o.order, fundedPct: o.fundedPct,
      asignado: o.assigned, apartar: o.setAsideToday, falta: o.missing, anios: o.isEmergencyFund ? null : o.years, estrategia: o.strategyFinal,
      bucket: dom.get(o.row) ?? null, fundedNeto: s.derived.cascadeNet ? (neto.get(o.row) ?? null) : undefined,
    }));
  const datos = { filas, currency: cur, cubiertos: s.strategy.objectivesCovered, falta: s.life.totals.missing, capital: s.life.freeCapital.investable, neto: !!s.derived.cascadeNet };
  const sinObjetivos = !s.life.objectives.some((o) => !o.isEmergencyFund && !isBlank(o.name));
  if (filas.length === 0) return vacio(datos, faltanDe(s, "2 · Vida"));
  const c = s.strategy.objectivesCovered;
  let lectura = c ? `Tu capital invertible de hoy (${fmtMoney(s.life.freeCapital.investable, cur)}) alcanza para ${c.n} de ${c.of} objetivos.` : "";
  const fondoRow = filas.find((f) => f.orden === 1);
  if (fondoRow && (fondoRow.fundedPct ?? 0) < 0.999) lectura += ` El fondo va al ${pct0(fondoRow.fundedPct)} y hasta que se llene no baja nada a los demás.`;
  if (s.life.totals.missing > 0.5) lectura += ` Lo que falta apartar en total: ${fmtMoney(s.life.totals.missing, cur)}.`;
  return { estado: (sinObjetivos ? "parcial" : "ok") as EstadoFig, lectura: lectura.trim(), faltan: sinObjetivos ? [{ cell: "2 · Vida!B29:B37", label: "tus objetivos patrimoniales" }] : [], datos };
}

// ───────────── 9 · Línea de vida ─────────────
export function lineaDeVida(s: Snapshot) {
  const hoy = s.setup.age, hasta = s.setup.targetAge;
  const marcas = s.life.objectives.filter((o) => !o.isEmergencyFund && !isBlank(o.name) && o.age !== null).map((o) => ({ x: o.age as number, etiqueta: o.name as string }));
  const franjas = s.life.film.filter((f) => f.from !== null && f.to !== null).map((f) => ({ desde: f.from as number, hasta: f.to as number, etiqueta: `${f.phase ?? ""}${f.source ? ` · ${f.source}` : ""}` }));
  const datos = { hoy, hasta, marcas, franjas };
  if (hoy === null || hasta === null) return vacio(datos, [{ cell: "Índice y setup!B7", label: "tu edad" }]);
  if (marcas.length === 0) return { estado: "parcial" as EstadoFig, lectura: `Tienes ${hoy} años y tu plan llega a los ${hasta}. Todavía no hay objetivos con edad.`, faltan: [{ cell: "2 · Vida!B29:B37", label: "tus objetivos patrimoniales" }], datos };
  const prox = [...marcas].filter((m) => m.x >= hoy).sort((a, b) => a.x - b.x)[0] ?? marcas[0];
  return { estado: "ok" as EstadoFig, lectura: `Tienes ${hoy} años. Tu objetivo más cercano: ${prox.etiqueta}, a los ${prox.x} (en ${Math.max(0, prox.x - hoy)} años). Horizonte del plan: ${hasta}.`, faltan: [], datos };
}

// ───────────── 10 · Proyección ─────────────
export function proyeccion(s: Snapshot) {
  const cur = s.setup.currency, p = s.strategy.projection;
  const usarEdad = s.setup.age !== null;
  const serie = [{ x: usarEdad ? (s.setup.age as number) : (s.setup.year ?? 0), y: p.start as number | null, y2: 0 }, ...p.series.map((r) => ({ x: (usarEdad ? r.age : r.year) ?? r.n, y: r.wealth, y2: r.contributed }))];
  const marcadores: { x: number; tipo: "meta" | "cero" | "corte"; etiqueta: string }[] = [];
  const meta = p.series.find((r) => r.status === "goal_reached");
  const cero = p.series.find((r) => r.status === "out_of_money");
  if (meta) marcadores.push({ x: (usarEdad ? meta.age : meta.year) ?? meta.n, tipo: "meta", etiqueta: "✅ meta" });
  if (cero) marcadores.push({ x: (usarEdad ? cero.age : cero.year) ?? cero.n, tipo: "cero", etiqueta: "⛔ se acaba" });
  const corte = p.series.find((r) => r.n === p.excelRows);
  if (corte && p.series.length > p.excelRows) marcadores.push({ x: (usarEdad ? corte.age : corte.year) ?? corte.n, tipo: "corte", etiqueta: "fin del Excel" });
  const datos = { serie, meta: p.goal > 0 ? p.goal : null, marcadores, xEtiqueta: usarEdad ? "Edad" : "Año", currency: cur };
  if (p.realRateUsed === null || p.series.length === 0 || p.series[0].wealth === null) return vacio(datos, [...faltanDe(s, "3B"), ...faltanDe(s, "2 · Vida!B29")]);
  const ultimo = p.series[p.series.length - 1];
  const tasa = `Con ${fmtPct(p.realRateUsed, 1)} real y ${p.contribution >= 0 ? `${fmtMoney(p.contribution, cur)} al año de aporte` : `retiros de ${fmtMoney(-p.contribution, cur)} al año`}`;
  let lectura: string;
  if (typeof p.ageAtGoal === "number") lectura = `${tasa}, llegas a tu meta (${fmtMoneyCorto(p.goal, cur)} en pesos de hoy) a los ${p.ageAtGoal}.`;
  else if (typeof p.ageAtZero === "number") lectura = `${tasa}, tu patrimonio se acaba a los ${p.ageAtZero}.`;
  else lectura = `${tasa}, a los ${ultimo.age ?? "—"} llegarías a ${fmtMoney(ultimo.wealth, cur)} (pesos de hoy)${p.goal > 0 ? `; la meta de ${fmtMoney(p.goal, cur)} queda más allá de los ${s.setup.targetAge}` : ""}.`;
  return { estado: "ok" as EstadoFig, lectura, faltan: [], datos };
}

// ───────────── 11 · Regla del 4 % ─────────────
export function regla4(s: Snapshot) {
  const cur = s.setup.currency, ph = s.photo;
  const datos = { capital: ph.investableNet, ingresoMensual: ph.sustainableIncomeMonthly, gastoMensual: s.flow.totals.expenseMonthly, necesario: ph.freedomTarget, pct: ph.freedomPotentialPct, currency: cur };
  if (ph.freedomTarget === null || ph.freedomTarget === 0) return vacio(datos, faltanDe(s, "3A"));
  return { estado: "ok" as EstadoFig, lectura: `Tu capital invertible (${fmtMoney(ph.investableNet, cur)}) sostiene ${fmtMoney(ph.sustainableIncomeMonthly, cur)} al mes con la regla del 4%: ${pct1(ph.freedomPotentialPct)} de tu gasto. Para cubrirlo todo necesitas ${fmtMoney(ph.freedomTarget, cur)}.`, faltan: [], datos };
}

// ───────────── 12 · IDP ─────────────
export function idp(s: Snapshot) {
  const pasos = s.idp.steps.map((st) => ({ etiqueta: st.label, valor: st.value, legado: st.legacy }));
  const datos = { pasos, score: s.idp.score, fecha: s.idp.answeredAt };
  if (s.idp.score === null) return vacio(datos, faltanDe(s, "0 · Diagnóstico"));
  const faltantes = s.idp.steps.filter((x) => x.value === null).map((x) => x.key.slice(1));
  const legado = s.idp.steps.filter((x) => x.legacy).map((x) => x.key.slice(1));
  const nivel = s.idp.score < 50 ? "a la deriva" : s.idp.score < 80 ? "tienes piezas, no diseño" : "diseño vivo";
  const lectura = `IDP: ${Math.round(s.idp.score)} de 100 (${nivel})${faltantes.length ? `; promedia solo los pasos contestados, falta el paso ${faltantes.join(" y ")}` : ""}.${legado.length ? ` Los pasos ${legado.join(" y ")} vienen de tu Excel de WealthX como promedio.` : ""}`;
  return { estado: (faltantes.length ? "parcial" : "ok") as EstadoFig, lectura, faltan: [], datos };
}

// ───────────── 13 · Rueda 360 ─────────────
export function rueda(s: Snapshot) {
  const areas = s.idp.wheel.map((w) => ({ etiqueta: w.label, valor: w.value, legado: w.legacy }));
  const datos = { areas, ir360: s.idp.ir360 };
  if (s.idp.ir360 === null) return vacio(datos, [{ cell: "0 · Diagnóstico!E96", label: "las 12 preguntas de la Rueda de Riqueza 360" }]);
  const con = s.idp.wheel.filter((w) => w.value !== null).sort((a, b) => (a.value as number) - (b.value as number));
  const faltan = s.idp.wheel.filter((w) => w.value === null).map((w) => w.label);
  const baja = con[0], alta = con[con.length - 1];
  const lectura = `IR360: ${Math.round(s.idp.ir360)} de 100. Tu área más baja hoy: ${baja.label} (${(baja.value as number).toFixed(1)})${con.length > 1 ? `; la más alta: ${alta.label} (${(alta.value as number).toFixed(1)})` : ""}.${baja.key === "emocional" ? " Si lo emocional está bajo, es muy probable que los pasos 1 y 2 de tu IDP también lo estén." : ""}${faltan.length ? ` Falta contestar ${faltan.join(" y ")}.` : ""}`;
  return { estado: (faltan.length ? "parcial" : "ok") as EstadoFig, lectura, faltan: [], datos };
}
